import { PageLayout } from '@ipfabric/ui-components/PageLayout/PageLayout';
import { prop } from '@ipfabric/utils/fp/prop';
import { pipe } from 'fp-ts/lib/function';
import { BoxTreeGallerySection } from 'pages/ComponentGallery/gallerySections/BoxTreeGallerySection';
import { CommandModelGallerySection } from 'pages/ComponentGallery/gallerySections/CommandModelGallerySection';
import { RateLimitingTestGallerySection } from 'pages/ComponentGallery/toolSections/RateLimitingTestGallerySection';
import { UpdateBatchingTestGallerySection } from 'pages/ComponentGallery/toolSections/UpdateBatchingTestGallerySection';
import type { ComponentType, FC } from 'react';
import { Link, useParams } from 'react-router-dom';
import { indexBy } from 'utils/object/indexBy';
import { DeviceAttributesFilterGallerySection } from './gallerySections/DeviceAttributesFilterGallerySection';
import { FileUploaderGallerySection } from './gallerySections/FileUploaderGallerySection';
import { FeatureFlagsGallerySection } from './toolSections/FeatureFlagsGallerySection';
import { SecuritySchemaSandboxGallerySection } from './toolSections/SecuritySchemaSandboxGallerySection';

interface ComponentGallerySection {
  url: string;
  name: string;
  component: ComponentType;
}

const componentGallerySections: ComponentGallerySection[] = [
  {
    url: 'box-tree',
    name: 'Box tree',
    component: BoxTreeGallerySection,
  },
  {
    url: 'command-model',
    name: 'Command model',
    component: CommandModelGallerySection,
  },
  {
    url: 'attributes-filter',
    name: 'Device attributes filter',
    component: DeviceAttributesFilterGallerySection,
  },
  {
    url: 'file-uploader',
    name: 'File uploader (Drag n Drop)',
    component: FileUploaderGallerySection,
  },
];

const toolSections: ComponentGallerySection[] = [
  {
    url: 'security-schema-sandbox',
    name: 'Security schema sandbox',
    component: SecuritySchemaSandboxGallerySection,
  },
  {
    url: 'update-batching-test',
    name: 'Update batching in React 18 test',
    component: UpdateBatchingTestGallerySection,
  },
  {
    url: 'rate-limiting-test',
    name: 'API rate limiting test',
    component: RateLimitingTestGallerySection,
  },
  {
    url: 'feature-flags',
    name: 'Feature flags',
    component: FeatureFlagsGallerySection,
  },
];

const sectionsByKey = pipe(
  [...componentGallerySections, ...toolSections],
  indexBy(prop('url')),
);

export const ComponentGalleryPage: FC = () => {
  const { sectionKey } = useParams();
  if (sectionKey === undefined) {
    const renderSectionLink = (section: ComponentGallerySection) => (
      <li key={section.url}>
        <Link to={`/component-gallery/${section.url}`}>{section.name}</Link>
      </li>
    );
    return (
      <PageLayout>
        <h2>Reusable components</h2>
        <p>
          Components or technical demos handling state or logic. Use storybook
          for UI components
        </p>
        <ul>{componentGallerySections.map(renderSectionLink)}</ul>
        <h2>Other internal tools</h2>
        <ul>{toolSections.map(renderSectionLink)}</ul>
      </PageLayout>
    );
  }

  const section = sectionsByKey[sectionKey];
  return (
    <PageLayout className='flex flex-col'>
      <p>
        <Link to='/component-gallery'>&lArr; back to gallery home</Link>
      </p>
      <h1 className='text-level-3 my-lg'>{section?.name}</h1>
      {/* Fixed noUncheckedIndexedAccess NIM-19408 */}
      {section !== undefined && <section.component />}
    </PageLayout>
  );
};
